import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { Storage } from '../services/storage';
import { AccountStore } from '../stores/account-store';
import type { WalletHomeRequest, WalletHomeResponse } from '../models';

/**
 * WalletHomeApi — HTTP calls for the Wallet page
 *
 * Single endpoint: POST /api/merchant/gozem_money/home
 *
 * Returns all wallet page data in one request:
 *   sections: account · wallet_accounts · services · banners · transaction
 *
 * Auth credentials are read automatically from localStorage via Storage service.
 */
@Injectable({
  providedIn: 'root',
})
export class WalletHomeApi {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(Storage);
  private readonly accountStore = inject(AccountStore);

  /**
   * Fetch the full Wallet home payload.
   *
   * @param page  - Page number for the transaction section (default: 1)
   * @param limit - Max transactions to return (default: 10)
   */
  getWalletHome(page = 1, limit = 10): Observable<WalletHomeResponse> {
    const userData = this.storage.getUserData();
    if (!userData) {
      return throwError(() => new Error('User data not found in storage'));
    }

    // Active account, not accounts[0]: pinning to the first row ignores which merchant
    // the user actually selected. See AccountStore.resolveAccountId.
    const accountId = this.accountStore.resolveAccountId();

    const payload: WalletHomeRequest = {
      merchant_id: userData.merchantId,
      account_id: accountId,
      merchant_user_id: userData.merchantUserId,
      user_type: 'merchant',
      device_type: 'web',
      token: userData.token,
      page,
      limit,
    };

    return this.http
      .post<WalletHomeResponse>(`${environment.apiUrl}/api/merchant/gozem_money/home`, payload)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => error)));
  }
}
